import { call, takeLatest, select, put, delay } from 'redux-saga/effects'
import { buyCheckoutActions, BuyCheckoutActionTypes } from '@buy/state/checkout/actions'
import { BuyCalculatePriceDataType, BuyCheckoutState } from '@buy/state/checkout/state'
import buyServices from '@buy/services'
import { getBillingAddressValues } from '@buy/utils/get-billing-address-values'
import { getCartItems } from '@buy/utils/get-item-values'
import { getSelectedAddons } from '@buy/saga/content/content-helpers'
import {
    getCheckoutState,
    getCouponState,
    getUserState,
    getPlanData,
    getProductSettingsData,
    getBuyFormState,
} from '@buy/state/selectors'
import { getLocationState, getAccountState } from '@common/state/selectors'
import { modalActions } from '@common/state/modal/actions'
import { fatalErrorActions } from '@common/state/fatal-error/actions'
import { BUY_TYPE, BUY_TRACKING_COMPONENTS, BUY_USER_TYPES } from '@buy/constants'
import { buyTrackingActions } from '@buy/state/tracking/actions'
import { TRACKING_EVENTS } from '@common/constants'
import { LocationState } from '@common/state/location/state'
import { BuyUserState } from '@buy/state/user/state'
import { BuyAddon } from '@buy/state/add-on'
import { BuyPlan } from '@buy/state/plan'
import { BuyCalculatePriceAPIReturnType } from '@buy/state/calculate-price'
import { isEmpty } from 'lodash-es'
import { getSubscriptionInfo } from '@buy/utils'
import { BuyCouponState } from '@buy/state/coupon/state'
import { ProductSettings } from '@buy/state/product-settings'
import { getTaxDetailValues } from '@buy/utils/get-tax-details'

export function* calculatePriceSaga(action: ReturnType<typeof buyCheckoutActions.calculatePrice>) {
    const componentName = BUY_TRACKING_COMPONENTS.CALCULATE_PRICE_SAGA
    try {
        yield put(buyCheckoutActions.setTotalLoading(true))
        yield delay(1000)
        const checkout: BuyCheckoutState = yield select(getCheckoutState)
        const { userType, existingBillingInformation }: BuyUserState = yield select(getUserState)
        const { values } = action.payload
        const planData: BuyPlan = yield select(getPlanData)
        const selectedPlan = planData
        const selectedAddons: BuyAddon[] = yield call(getSelectedAddons)
        const coupon: BuyCouponState = yield select(getCouponState)
        const { isTaxNumberValid } = yield select(getBuyFormState)
        const { isCouponValidAndApplied } = coupon
        const isCouponValid = isCouponValidAndApplied && coupon.couponServerData && !isEmpty(coupon.couponServerData)
        const { productFamily }: ProductSettings = yield select(getProductSettingsData)

        if (userType === BUY_USER_TYPES.ADDON) {
            values.Street1 = existingBillingInformation?.addressLine1 || ''
            values.City = existingBillingInformation?.city || ''
            values.CountryCode = existingBillingInformation?.country || ''
            values.Email = existingBillingInformation?.email || ''
            values.FirstName = existingBillingInformation?.firstName || ''
            values.LastName = existingBillingInformation?.lastName || ''
            values.ZipCode = existingBillingInformation?.postalCode || ''
            values.StateCode = existingBillingInformation?.state || ''
            values.TaxNumber = existingBillingInformation?.taxNumber || ''
            values.TaxType = existingBillingInformation?.federalTaxType || ''
        }

        const { locationInfo }: LocationState = yield select(getLocationState)
        const { accountSubscriptions } = yield select(getAccountState)

        const subscriptionInfo = getSubscriptionInfo(
            checkout.billingTenure.selected,
            selectedPlan,
            planData,
            accountSubscriptions
        )

        if (!subscriptionInfo.isReplacementBooking && !selectedAddons.length) {
            yield put(buyCheckoutActions.setTotalLoading(false))
            yield put(buyCheckoutActions.setCalculatedPrice([], 0, 0, 0, 0))
            yield put(buyCheckoutActions.setProratedAmount(0))
            return
        }
        const taxDetails = isTaxNumberValid ? getTaxDetailValues(values) : null
        const calculatePriceValues: BuyCalculatePriceDataType = {
            address: getBillingAddressValues(values),
            currency: locationInfo.currency,
            couponCode: isCouponValid ? coupon.couponServerData?.couponCode : '',
            cart: getCartItems(
                checkout.baseLine,
                checkout.additionalLines,
                checkout.addonLines,
                subscriptionInfo,
                checkout.billingTenure.selected,
                productFamily
            ),

            // getPaymentItems(selectedPlan, 1, checkout.billingFrequency, selectedAddons, subscriptionInfo),
        }
        if (isTaxNumberValid && taxDetails) {
            calculatePriceValues.taxDetails = taxDetails
        }

        const calculatePriceCreation: BuyCalculatePriceAPIReturnType = yield call(
            buyServices.calculatePrice,
            calculatePriceValues
        )

        if (calculatePriceCreation && calculatePriceCreation.data) {
            // TODO: WAE1-4451: Finish migrating from the eCommAPI response format to the UCS format
            // See example responses here: https://confluence.ops.expertcity.com/spaces/COM/pages/783450418/Calculate+Price+Net+New+Customer
            const { errors, productPrices, netAmount, grossAmount, taxAmount, totalCreditBack } =
                calculatePriceCreation.data
            if (isEmpty(errors)) {
                yield put(
                    buyCheckoutActions.setCalculatedPrice(
                        productPrices,
                        netAmount,
                        grossAmount,
                        taxAmount,
                        totalCreditBack
                    )
                )
            } else {
                yield put(
                    buyTrackingActions.track(
                        {
                            event: TRACKING_EVENTS.ERROR_PURCHASE,
                            eventData: {
                                errorMessage: 'Sorry we are having trouble applying your credit',
                                buyType:
                                    subscriptionInfo.isTrial || !subscriptionInfo.plan
                                        ? BUY_TYPE.DIRECT_BUY
                                        : BUY_TYPE.ADDON,
                                plan: checkout.billingTenure.selected,
                            },
                        },
                        componentName
                    )
                )
                yield put(modalActions.setErrorModal('Unable to get price data'))
            }
        }
        yield put(buyCheckoutActions.setTotalLoading(false))
    } catch {
        yield put(
            buyTrackingActions.track(
                { event: TRACKING_EVENTS.ERROR_PURCHASE, eventData: { errorMessage: 'error in calculating price' } },
                componentName
            )
        )
        yield put(fatalErrorActions.setFatalError(true, { trackErrorMessage: 'error in calculating price' }))
        yield put(buyCheckoutActions.setTotalLoading(false))
    }
}

function* initializeCalculatePriceSaga() {
    yield takeLatest(BuyCheckoutActionTypes.CALCULATE_PRICE, calculatePriceSaga)
}

export default initializeCalculatePriceSaga
